import React, { ReactNode } from "react";
import { XIcon } from "@heroicons/react/outline";

interface IModalProps {
  setShowModal: (value: boolean) => void;
  title: string;
  children: ReactNode;
  enableSubmitButton?: boolean;
  contentType?: "form" | "slide";
  submitLabel?: string;
}

const getWidthClasses = (type: IModalProps["contentType"]) =>
  type === "slide" ? "sm:max-w-3xl sm:w-full" : "sm:max-w-lg sm:w-full";

const PrimaryButton = ({ children, ...props }) => (
  <button
    type="submit"
    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
    {...props}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, ...props }) => (
  <button
    type="button"
    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
    {...props}
  >
    {children}
  </button>
);

export const Modal = ({
  setShowModal,
  title,
  children,
  enableSubmitButton = true,
  contentType = "form",
  submitLabel = "Associate",
}: IModalProps) => {
  const widthClasses = getWidthClasses(contentType);

  return (
    <div
      className="fixed inset-0 z-10 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          aria-hidden="true"
        ></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div
          className={`inline-block h-auto px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle ${widthClasses} sm:p-6`}
        >
          <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
            <button
              onClick={() => setShowModal(false)}
              type="button"
              className="text-gray-400 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary"
            >
              <XIcon className="w-6 h-6" aria-hidden="true" />
            </button>
          </div>

          <div className="flex border-b">
            <h2 className="text-xl font-semibold text-gray-700">{title}</h2>
          </div>

          <div className="grid grid-cols-1 mt-3">{children}</div>

          {enableSubmitButton && (
            <div className="flex pt-4">
              <PrimaryButton>{submitLabel}</PrimaryButton>
              <SecondaryButton onClick={() => setShowModal(false)}>
                Cancel
              </SecondaryButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
